import { CloseCircleFilled, SearchOutlined } from '@ant-design/icons';
import { Button, Col, Input, Row } from 'antd';
import casinoApi from 'api/casinoApi';
import { useAppDispatch, useAppSelector, useAppTranslation } from 'app/hooks';
import EmptyTableIcon from 'assets/icons/EmptyTableIcon';
import ConditionButton from 'components/Commons/ConditionButton';
import FooterMenu from 'components/Commons/FooterMenu';
import Header from 'components/Commons/Header';
import LoadingSpinner from 'components/Commons/LoadingSpinner';
import AuthPage from 'features/auth/pages/AuthPage';
import ChangePasswordPage from 'features/auth/pages/ChangePasswordPage';
import TransactionModal from 'features/home/components/Modals/TransactionModal';
import { onChangeAuthPage, setShowSubMenu } from 'features/home/homeSlice';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import useUserDetail from 'hooks/useUserDetail';
import { AwcGame } from 'models/casino';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { selectPlatforms } from 'redux/casinoSlice';
import { sortGame } from 'utils/casinoUtil';

interface AllGamePageProps {}

const AllGamePage: React.FunctionComponent<AllGamePageProps> = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const handleResponseError = useHandleResponseError();
  const { isUserLoggedIn } = useUserDetail();
  const dispatch = useAppDispatch();
  const platforms = useAppSelector(selectPlatforms);
  const t = useAppTranslation();

  const [isShowChangePasswordForm, setIsShowChangePasswordForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [games, setGames] = useState<AwcGame[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [gameType, setGameType] = useState<string>();
  const platform: string = useMemo(() => {
    return searchParams.get('platform') || 'BTG';
  }, [searchParams]);

  const fetchData = useCallback(
    async (customSearchText = searchText) => {
      setIsLoading(true);
      setGames([]);
      if (!!gameType) {
        const { ok, body, error, pagination } = await casinoApi.getGame({
          size: 99999,
          page: 0,
          sort: 'displayOrder,asc',
          'gameType.equals': gameType,
          'platform.equals': platform,
          'gameName.contains': customSearchText || null,
          'isMiniGame.equals': false,
        });

        if (ok && body && pagination) {
          setGames(sortGame(body));
        }
        setIsLoading(false);
        handleResponseError(error);
      }
    },
    [platform, gameType, searchText],
  );

  useEffect(() => {
    fetchData();
  }, [platform, gameType]);

  const onShowChangePasswordPage = () => {
    if (isUserLoggedIn) setIsShowChangePasswordForm(true);
  };

  const closeForm = (): void => {
    dispatch(onChangeAuthPage(-1));
    dispatch(setShowSubMenu(false));
    setIsShowChangePasswordForm(false);
  };

  const handleClickGameInMobile = async (game: AwcGame) => {
    setIsLoading(true);
    const { ok, body, error } = await casinoApi.playGame({
      gameCode: game.gameCode,
      mobileLogin: window.innerWidth < 1025,
      provider: game.provider,
    });

    if (ok && body) {
      window.open(body.url, '_blank');
    }
    if (error) {
      handleResponseError(error);
    }
    setIsLoading(false);
  };

  const handleChangeCategory = (key: string) => {
    setSearchText('');
    setGameType(key);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const onSubmit = async () => {
    setGames([]);
    await fetchData();
  };

  const gameCategories = useMemo(() => {
    return platforms.find((p) => p.platform === platform);
  }, [platform, platforms.length]);

  const onClearSearch = () => {
    setSearchText('');
    fetchData('');
  };
  useEffect(() => {
    if (gameCategories) {
      setGameType(gameCategories.games[0].gameType);
    }
  }, [gameCategories]);
  return (
    <div className="all-game-page relative">
      <i className="version">Version 1.0</i>
      {isLoading && (
        <div className="overlay">
          <LoadingSpinner />
        </div>
      )}
      <div className={`body-waraper`}>
        <Header isCasino onShowChangePasswordPage={onShowChangePasswordPage} isShowAllPage />

        <div className="casino-search">
          <Input
            placeholder={t('common.search')}
            className="search-input"
            prefix={
              searchText ? (
                <CloseCircleFilled onClick={onClearSearch} style={{ cursor: 'pointer' }} />
              ) : (
                <SearchOutlined style={{ fontSize: '20px' }} className="search-icon" />
              )
            }
            value={searchText}
            onChange={onChange}
            onPressEnter={onSubmit}
          />
        </div>

        {gameCategories && (
          <div className="game-category">
            {gameCategories.games.map((c) => (
              <Button
                key={`game-category-${c.id}`}
                className={`game-category__btn ${gameType === c.gameType ? 'active' : ''}`}
                onClick={() => handleChangeCategory(c.gameType)}
              >
                {c.gameType}
              </Button>
            ))}
          </div>
        )}

        <div className="casino-game-list search-casino-game">
          {games.length > 0 ? (
            <Row className="relative" gutter={[16, 16]} wrap={true}>
              {games
                .sort((a, b) => a.order - b.order)
                .map((game) => (
                  <Col
                    lg={{ span: 4 }}
                    md={{ span: 4 }}
                    sm={{ span: 6 }}
                    xs={{ span: 8 }}
                    className="casino-game-item"
                    key={`new-game-${game.gameCode}`}
                  >
                    <ConditionButton
                      className="play-button"
                      onClick={() => handleClickGameInMobile(game)}
                      content={
                        <div className="casino-game-image">
                          <img
                            onError={(e) => {
                              e.currentTarget.src = '/no_image.png';
                            }}
                            src={game.image}
                            alt="preview"
                          />
                        </div>
                      }
                      testerCheck={true}
                    />

                    <span className="casino-game-title">{game.name}</span>
                  </Col>
                ))}
            </Row>
          ) : (
            <div className="empty-table">
              <EmptyTableIcon />
            </div>
          )}
        </div>

        <FooterMenu isCasino onShowChangePasswordPage={onShowChangePasswordPage} />
      </div>
      <ChangePasswordPage isShow={isShowChangePasswordForm} closePage={closeForm} />
      <AuthPage />
      <TransactionModal />
    </div>
  );
};

export default AllGamePage;
